<template>
  <div class="custom-table-container w-100">
    <!-- Search -->
    <div
      class="d-flex justify-content-between align-items-center mb-3 flex-wrap"
      v-if="heading || buttons.length > 0 || showSearch"
    >
      <div class="d-flex align-items-center me-3 ms-1 mb-sm-0">
        <p v-if="heading" class="mb-0 fw-bold">{{ heading }}</p>
      </div>

      <div class="d-flex align-items-center me-1">
        <div class="d-flex align-items-center me-2">
          <button
            v-for="(button, index) in buttons"
            :key="'btn-' + index"
            :class="[
              'btn btn-sm rounded-3 shadow fw-bold text-xs me-2',
              button.class || 'btn-outline-primary',
            ]"
            @click="handleButtonClick(button.action, $event)"
            :title="button.title"
            :disabled="button.disabled"
          >
            <i v-if="button.icon" :class="[button.icon, 'me-1']"></i>
            {{ button.label }}
          </button>
        </div>

        <!-- Header Actions Slot -->
        <slot name="header-actions"></slot>

        <div v-if="showSearch">
          <SearchBar
            v-model="search"
            placeholder="Search..."
            label="Search"
            @update:modelValue="onSearch"
          />
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="table-responsive">
      <table class="table table-striped table-hover w-100">
        <thead>
          <tr>
            <th>#</th>
            <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
            <th v-if="hasActions">Action</th>
            <th v-if="showCheckboxes" class="checkbox-column">
              <slot name="select-all"></slot>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Loading skeleton rows -->
          <template v-if="showSkeleton">
            <tr
              v-for="index in Array.isArray(skeletonRows)
                ? skeletonRows.length
                : skeletonRows || 5"
              :key="'skeleton-' + index"
              class="skeleton-row"
            >
              <td>
                <div class="skeleton skeleton-text"></div>
              </td>
              <td v-for="col in columns" :key="col.key">
                <div class="skeleton skeleton-text"></div>
              </td>
              <td v-if="hasActions">
                <div class="skeleton skeleton-button"></div>
              </td>
              <td v-if="showCheckboxes">
                <div class="skeleton skeleton-checkbox"></div>
              </td>
            </tr>
          </template>
          <template v-else-if="showNoData">
            <tr>
              <td
                :colspan="columns.length + (hasActions ? 2 : 1) + (showCheckboxes ? 1 : 0)"
                class="text-center py-4"
              >
                <div class="text-muted">
                  <i class="fas fa-inbox fa-2x mb-2"></i>
                  <p class="mb-0">No data available.</p>
                </div>
              </td>
            </tr>
          </template>
          <template v-else>
            <template v-for="(row, index) in rows" :key="row.id">
              <tr :class="[{ 'selected-row': selectedRowId && selectedRowId === row.id }, rowClass ? rowClass(row) : '']">
                <td>
                  <slot name="cell-rownum" :row="row" :index="index">
                    {{ (meta.page - 1) * meta.per_page + index + 1 }}
                  </slot>
                </td>
                <td v-for="col in columns" :key="col.key">
                  <slot :name="'cell-' + col.key" :row="row">
                    {{ row[col.key] }}
                  </slot>
                </td>
                <td v-if="hasActions">
                  <slot name="actions" :row="row"></slot>
                </td>
                <td v-if="showCheckboxes">
                  <slot name="checkbox" :row="row"></slot>
                </td>
              </tr>
              <tr v-if="expandedRowId === row.id" :key="row.id + '-expanded'">
                <td :colspan="columns.length + (hasActions ? 2 : 1) + (showCheckboxes ? 1 : 0)">
                  <slot name="expandedRow" :row="row"></slot>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="meta.total > meta.per_page && !initialLoad" class="d-flex justify-content-center">
      <div class="pagination-badge">
        <button 
          class="page-btn" 
          :disabled="meta.page === 1 || loading"
          @click="changePage(meta.page - 1)"
          title="Previous"
        >
          <i class="fas fa-chevron-left"></i>
        </button>

        <template v-for="page in visiblePages" :key="page + '-page'">
          <button
            v-if="page !== '...'"
            class="page-btn"
            :class="{ active: page === meta.page && !loading }"
            @click="changePage(page)"
          >
            {{ page }}
          </button>
          <span v-else class="page-separator">...</span>
        </template>

        <button 
          class="page-btn"
          :disabled="meta.page === totalPages || loading"
          @click="changePage(meta.page + 1)"
          title="Next"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Results info -->
    <div v-if="!initialLoad" class="text-end text-xs">
      <template v-if="loading && rows.length > 0">
        <i class="fas fa-spinner fa-spin me-1"></i>
        Updating...
      </template>
      <template v-else-if="meta.total > 0">
        Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries
      </template>
    </div>
  </div>
</template>
<script>
  import "@/assets/css/index.css";
  import SearchBar from "@/components/SearchBar/SearchBar.vue";

  export default {
    name: "CustomTable",
    components: {
      SearchBar,
    },
    props: {
      fetchData: {
        type: Function,
        required: true,
      },
      showSearch: {
        type: Boolean,
        default: true,
      },
      columns: {
        type: Array,
        required: true,
      },
      hasActions: {
        type: Boolean,
        default: false,
      },
      tableKey: {
        type: String,
        required: true,
      },
      heading: {
        type: String,
        default: "",
      },
      buttons: {
        type: Array,
        default: () => [],
        // Example structure:
        // [{ label: 'Add', icon: 'fas fa-plus', class: 'btn-success', action: 'add-new' }]
      },
      expandedRowId: {
        type: [String, Number],
        default: null,
      },
      selectedRowId: {
        type: [String, Number],
        default: null,
      },
      rowClass: {
        type: Function,
        default: null,
      },
      showCheckboxes: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        rows: [],
        meta: {
          page: 1,
          per_page: 10,
          total: 0,
          from: 0,
          to: 0,
        },
        search: "",
        searchDebounce: null,
        loading: false,
        initialLoad: true,
      };
    },
    computed: {
      totalPages() {
        return Math.ceil(this.meta.total / this.meta.per_page);
      },
      visiblePages() {
        const pages = [];
        const total = this.totalPages;
        const current = this.meta.page;

        if (total <= 7) {
          for (let i = 1; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          if (current > 4) pages.push("...");
          for (
            let i = Math.max(2, current - 1);
            i <= Math.min(total - 1, current + 1);
            i++
          ) {
            pages.push(i);
          }
          if (current < total - 3) pages.push("...");
          pages.push(total);
        }
        return pages;
      },
      skeletonRows() {
        return Array.from({ length: this.meta.per_page }, (_, index) => ({
          id: `skeleton-${index}`,
        }));
      },
      showSkeleton() {
        return this.loading || (this.initialLoad && this.rows.length === 0);
      },
      showNoData() {
        return !this.loading && !this.initialLoad && this.rows.length === 0;
      },
    },
    methods: {
      handleButtonClick(action, event) {
        this.$emit("button-click", { action, event });
      },
      async loadData() {
        try {
          this.loading = true;
          const response = await this.fetchData(this.meta.page, this.search);
          this.rows = response.results;
          this.meta.total = response.count;
          this.meta.from = (this.meta.page - 1) * this.meta.per_page + 1;
          this.meta.to = this.meta.from + response.results.length - 1;
          this.initialLoad = false;
          localStorage.setItem(`page_${this.tableKey}`, this.meta.page);
        } catch (error) {
          console.error("Error loading data:", error);
          this.rows = [];
          this.meta.total = 0;
          this.initialLoad = false;
        } finally {
          this.loading = false;
          this.$nextTick(() => {
            if (this.$refs.tableContainer) {
              const input =
                this.$refs.tableContainer.querySelector('input[type="text"]');
              if (input) input.focus();
            }
          });
        }
      },
      changePage(page) {
        if (page < 1 || page > this.totalPages || this.loading) return;
        this.meta.page = page;
        this.loadData();
      },
      onSearch() {
        if (this.loading) return;
        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => {
          this.meta.page = 1;
          this.loadData();
        }, 500);
      },
      reload() {
        this.meta.page = 1;
        this.loadData();
      },
    },
    mounted() {
      this.meta.page = 1;
      this.loadData();
    },
  };
</script>

<style scoped>
.pagination-badge {
  background: #f8f9fa;
  border: 1.5px solid #dee1e5;
  border-radius: 1rem;
  padding: 0.2rem 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.page-btn {
  background: transparent;
  border: none;
  color: #6c757d;
  font-weight: 600;
  font-size: 0.85rem;
  min-width: 28px;
  height: 28px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0 0.4rem;
}

.page-btn:hover:not(:disabled):not(.active) {
  color: #495057;
  background: rgba(0, 0, 0, 0.05);
}

.page-btn.active {
  background: white;
  color: var(--primary-color, #0d6efd);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.page-btn:disabled {
  color: #adb5bd;
  cursor: not-allowed;
  opacity: 0.6;
}

.page-separator {
  color: #adb5bd;
  font-size: 0.85rem;
  padding: 0 0.2rem;
  user-select: none;
}

.selected-row {
  background: linear-gradient(90deg, rgba(var(--primary-color-rgb, 13, 110, 253), 0.25) 0%, rgba(var(--primary-color-rgb, 13, 110, 253), 0.1) 100%) !important;
}

.selected-row td {
  background-color: transparent !important;
}

/* Unread Row Styling - Border indicator like selected-row */
.unread-row {
  border-left: 3px solid var(--primary-color, #0d6efd) !important;
}

.unread-row td {
  font-weight: 600;
}

.checkbox-column {
  width: 40px;
}

.skeleton-checkbox {
  width: 18px;
  height: 18px;
}
</style>
